import React, { useState } from 'react';
import { Plus, X, Trash2 } from 'lucide-react';

/**
 * FilterBuilder Component
 * Helps users build filters for report sections with parameter support
 */
const FilterBuilder = ({ filters = [], parameters = [], entityFields = [], onChange }) => {
  const operators = [
    { value: '=', label: 'Equals (=)' },
    { value: '!=', label: 'Not Equals (!=)' },
    { value: '>', label: 'Greater Than (>)' },
    { value: '<', label: 'Less Than (<)' },
    { value: '>=', label: 'Greater or Equal (>=)' },
    { value: '<=', label: 'Less or Equal (<=)' },
    { value: 'LIKE', label: 'Contains (LIKE)' },
    { value: 'IN', label: 'In List (IN)' },
    { value: 'IS NULL', label: 'Is Null' },
    { value: 'IS NOT NULL', label: 'Is Not Null' }
  ];

  const handleAddFilter = () => {
    const newFilter = {
      field: entityFields[0]?.name || '',
      operator: '=',
      value: '',
      useParameter: false,
      parameterName: ''
    };
    onChange([...filters, newFilter]);
  };

  const handleUpdateFilter = (index, updates) => {
    const updatedFilters = [...filters];
    updatedFilters[index] = { ...updatedFilters[index], ...updates };
    onChange(updatedFilters);
  };

  const handleDeleteFilter = (index) => {
    const updatedFilters = filters.filter((_, i) => i !== index);
    onChange(updatedFilters);
  };

  const renderValueInput = (filter, index) => {
    // If using parameter, show parameter selector
    if (filter.useParameter) {
      return (
        <div className="space-y-2">
          <select
            value={filter.parameterName || ''}
            onChange={(e) => {
              const paramName = e.target.value;
              handleUpdateFilter(index, {
                parameterName: paramName,
                value: `{{${paramName}}}`
              });
            }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="">Select Parameter</option>
            {parameters.map(param => (
              <option key={param.name} value={param.name}>
                {param.label} ({`{{${param.name}}}`})
              </option>
            ))}
          </select>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Filter value: <code className="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded">{filter.value || '(none)'}</code>
          </div>
        </div>
      );
    }

    // Static value input
    if (filter.operator === 'IS NULL' || filter.operator === 'IS NOT NULL') {
      return (
        <div className="text-sm text-gray-500 dark:text-gray-400 italic">
          No value needed for this operator
        </div>
      );
    }

    return (
      <input
        type="text"
        value={filter.value || ''}
        onChange={(e) => handleUpdateFilter(index, { value: e.target.value })}
        placeholder="Enter value or use {{parameter}}"
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100 font-mono text-sm"
      />
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Filters
        </label>
        <button
          onClick={handleAddFilter}
          className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-1"
        >
          <Plus className="w-3 h-3" />
          Add Filter
        </button>
      </div>

      {filters.length === 0 && (
        <div className="text-center py-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No filters defined. Click "Add Filter" to create one.
          </p>
        </div>
      )}

      {filters.map((filter, index) => (
        <div
          key={index}
          className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2"
        >
          {/* Field Selection */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Field
            </label>
            <select
              value={filter.field}
              onChange={(e) => handleUpdateFilter(index, { field: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
            >
              {entityFields.length === 0 && <option value="">Select entity first</option>}
              {entityFields.map(field => (
                <option key={field.name} value={field.name}>
                  {field.label || field.name}
                </option>
              ))}
            </select>
          </div>

          {/* Operator Selection */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Operator
            </label>
            <select
              value={filter.operator}
              onChange={(e) => handleUpdateFilter(index, { operator: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
            >
              {operators.map(op => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>
          </div>

          {/* Value Input with Parameter Toggle */}
          {filter.operator !== 'IS NULL' && filter.operator !== 'IS NOT NULL' && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                  Value
                </label>
                {parameters.length > 0 && (
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filter.useParameter || false}
                      onChange={(e) => handleUpdateFilter(index, {
                        useParameter: e.target.checked,
                        value: e.target.checked ? '' : filter.value,
                        parameterName: ''
                      })}
                      className="w-3 h-3 text-blue-600 rounded"
                    />
                    <span className="text-xs text-gray-600 dark:text-gray-400">Use Parameter</span>
                  </label>
                )}
              </div>
              {renderValueInput(filter, index)}
            </div>
          )}

          {/* Delete Button */}
          <div className="flex justify-end pt-1">
            <button
              onClick={() => handleDeleteFilter(index)}
              className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex items-center gap-1"
            >
              <Trash2 className="w-3 h-3" />
              Remove
            </button>
          </div>
        </div>
      ))}

      {/* Help Text */}
      {parameters.length > 0 && filters.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-2">
          <p className="text-xs text-blue-800 dark:text-blue-200">
            <strong>Tip:</strong> Check "Use Parameter" to use dynamic values from parameters, or manually enter {`{{paramName}}`} in the value field.
          </p>
        </div>
      )}

      {parameters.length === 0 && filters.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-2">
          <p className="text-xs text-yellow-800 dark:text-yellow-200">
            <strong>No parameters defined.</strong> Click the "Parameters" button in the toolbar to add parameters that can be used in filters.
          </p>
        </div>
      )}
    </div>
  );
};

export default FilterBuilder;

